import { imageService } from '@/services/imageService';

// TinyMCE Custom Upload Plugin
export const setupImageUpload = (editor: any) => {
    // Handle paste events for images
    editor.on('paste', async (e: any) => {
        const clipboardData = e.clipboardData || (e.originalEvent && e.originalEvent.clipboardData);

        // Case 1: Native files in clipboard (Chrome/Edge/Firefox)
        if (clipboardData && (clipboardData.files?.length || clipboardData.items?.length)) {
            const items = clipboardData.files?.length ? clipboardData.files : clipboardData.items;
            for (let i = 0; i < items.length; i++) {
                const item: any = items[i];
                const type = item.type || item.kind;
                if ((type && String(type).includes('image')) || item.kind === 'file') {
                    const file = item.getAsFile ? item.getAsFile() : item;
                    if (file && file.type?.startsWith('image/')) {
                        e.preventDefault();
                        uploadImageToBackend(file, editor);
                    }
                }
            }
        }

        // Case 1b: Plain text is an image URL (user pasted URL like @http://... or http://...png)
        try {
            const text = clipboardData?.getData?.('text/plain')?.trim();
            if (text) {
                // Normalize leading characters such as '@'
                const normalized = text.replace(/^[@\s<]+/, '').replace(/[>]+$/, '');
                const looksLikeImageUrl = /^(https?:\/\/).+\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(normalized);
                if (looksLikeImageUrl) {
                    e.preventDefault();
                    try {
                        const filename = extractFilenameFromUrl(normalized) || `pasted-${Date.now()}.png`;
                        const resp: any = await imageService.createFromUrl({ url: normalized, title: filename, alt: filename, linkImage: '', rel: 'nofollow', target: '_self' });
                        const data = resp?.data?.data?.data || resp?.data;
                        const uploadedUrl = data?.url;
                        if (uploadedUrl) {
                            editor.insertContent(`<img src="${uploadedUrl}" alt="${filename}" style="max-width:100%;height:auto;" />`);
                            return;
                        }
                    } catch {
                        // Fallback: fetch and upload as file
                        const filename = extractFilenameFromUrl(normalized) || `pasted-${Date.now()}.png`;
                        const file = await urlToFile(normalized, filename);
                        if (file) {
                            await uploadImageToBackend(file, editor);
                        }
                    }
                }
            }
        } catch {
            // ignore
        }
    });

    // Case 2: Pasted content contains base64 data URLs (Word, some OS)
    editor.on('PastePostProcess', async (e: any) => {
        try {
            const container: HTMLElement = e.node as HTMLElement;
            const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
            for (const img of imgs) {
                const src = img.getAttribute('src') || '';
                if (src.startsWith('data:image/')) {
                    const file = await dataUrlToFile(src, `pasted-${Date.now()}.png`);
                    const url = await uploadAndGetUrl(file, editor);
                    if (url) {
                        img.setAttribute('src', url);
                    }
                } else if (/^https?:\/\//i.test(src)) {
                    // External image URL -> prefer backend create-from-url (faster, no CORS)
                    try {
                        const filename = extractFilenameFromUrl(src) || `pasted-${Date.now()}.png`;
                        const resp: any = await imageService.createFromUrl({ url: src, title: filename, alt: filename, linkImage: '', rel: 'nofollow', target: '_self' });
                        const data = resp?.data?.data?.data || resp?.data;
                        const uploadedUrl = data?.url;
                        if (uploadedUrl) {
                            img.setAttribute('src', uploadedUrl);
                            continue;
                        }
                    } catch {
                        // fallback to fetch->file
                        const filename = extractFilenameFromUrl(src) || `pasted-${Date.now()}.png`;
                        const file = await urlToFile(src, filename);
                        if (file) {
                            const url = await uploadAndGetUrl(file, editor);
                            if (url) {
                                img.setAttribute('src', url);
                            }
                        }
                    }
                }
            }
        } catch (err) {
            // ignore
        }
    });

    // Handle drop events for images
    editor.on('drop', (e: any) => {
        const dataTransfer = e.dataTransfer;

        if (dataTransfer && dataTransfer.files) {
            for (let i = 0; i < dataTransfer.files.length; i++) {
                const file = dataTransfer.files[i];

                if (file.type.startsWith('image/')) {
                    e.preventDefault();
                    uploadImageToBackend(file, editor);
                }
            }
        }
    });

    // Handle drag over events
    editor.on('dragover', (e: any) => {
        e.preventDefault();
    });
};

// Upload image to backend
const uploadImageToBackend = async (file: File, editor: any) => {
    try {
        // Show loading indicator
        const loadingId = 'uploading-' + Date.now();
        editor.insertContent(`<div id="${loadingId}" style="border: 2px dashed #ccc; padding: 20px; text-align: center; color: #666;">
      <p>📤 Đang tải lên ảnh...</p>
    </div>`);

        // Upload using imageService
        const response: any = await imageService.upload(file, {
            title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
            alt: file.name,
            caption: "",
            linkImage: "",
            rel: "nofollow",
            target: "_self",
        });



        if (response.ok && response.data) {

            // Handle nested response structure - check if data has nested data property
            const imageData = response?.data?.data?.data || response.data;
            console.log("imageData: ", imageData)
            const imageUrl = imageData?.url;
            console.log("imageUrl: ", imageUrl)

            if (!imageUrl) {
                throw new Error('No image URL in response');
            }

            // Replace loading indicator with actual image
            const imageHtml = `<img src="${imageUrl}" data-image-id="${imageData?._id || ''}" alt="${imageData.alt || file.name}" style="max-width: 100%; height: auto;" />`;

            // Method 1: Try regex replacement
            const content = editor.getContent();
            const loadingDivRegex = new RegExp(`<div[^>]*id="${loadingId}"[^>]*>[\\s\\S]*?<\\/div>`, 'g');
            let newContent = content.replace(loadingDivRegex, imageHtml);

            console.log('Original content:', content);
            console.log('New content after regex:', newContent);

            // Method 2: If regex didn't work, try direct string replacement
            if (newContent === content) {
                console.log('Regex failed, trying direct replacement...');
                const loadingDivHtml = `<div id="${loadingId}" style="border: 2px dashed #ccc; padding: 20px; text-align: center; color: #666;">
      <p>📤 Đang tải lên ảnh...</p>
    </div>`;
                newContent = content.replace(loadingDivHtml, imageHtml);
                console.log('New content after direct replacement:', newContent);
            }

            // Method 3: If still not working, use TinyMCE's insertContent
            if (newContent === content) {
                console.log('Both methods failed, using insertContent...');
                // Remove the loading div first
                const body = editor.getBody();
                const loadingDiv = body.querySelector(`#${loadingId}`);
                if (loadingDiv) {
                    loadingDiv.remove();
                }
                // Insert the image
                editor.insertContent(imageHtml);
            } else {
                editor.setContent(newContent);
            }

            // Show success message
            editor.notificationManager.open({
                text: '✅ Ảnh đã được tải lên thành công!',
                type: 'success',
                timeout: 3000,
            });
        } else {
            throw new Error('Upload failed');
        }
    } catch (error) {
        console.error('Upload error:', error);

        // Remove loading indicator
        const content = editor.getContent();
        const newContent = content.replace(
            /<div id="uploading-\d+"[^>]*>[\s\S]*?<\/div>/g,
            ''
        );
        editor.setContent(newContent);

        // Show error message
        editor.notificationManager.open({
            text: '❌ Lỗi khi tải lên ảnh. Vui lòng thử lại.',
            type: 'error',
            timeout: 5000,
        });
    }
};

// Register the plugin
export const imageUploadPlugin = (editor: any) => {
    setupImageUpload(editor);
};

// Last-resort safety net: quét HTML nội dung trước khi submit và upload mọi
// ảnh base64 còn sót lại (vd: do trình duyệt/paste path nào đó chưa bắt được),
// tránh gửi content khổng lồ lên server rồi mới bị chặn bởi giới hạn fieldSize.
export const sanitizeEditorContent = async (html: string): Promise<string> => {
    if (!html || !html.includes('data:image/')) return html;

    const container = document.createElement('div');
    container.innerHTML = html;
    const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];

    for (const img of imgs) {
        const src = img.getAttribute('src') || '';
        if (!src.startsWith('data:image/')) continue;
        try {
            const file = await dataUrlToFile(src, `content-${Date.now()}.png`);
            const url = await uploadAndGetUrl(file, null);
            if (url) {
                img.setAttribute('src', url);
            } else {
                img.remove();
            }
        } catch {
            img.remove();
        }
    }

    return container.innerHTML;
};

// Helpers
const dataUrlToFile = async (dataUrl: string, filename: string): Promise<File> => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const fileType = blob.type || 'image/png';
    return new File([blob], filename, { type: fileType });
};

const uploadAndGetUrl = async (file: File, _editor: any): Promise<string | null> => {
    try {
        const resp: any = await imageService.upload(file, {
            title: file.name.replace(/\.[^/.]+$/, ""),
            alt: file.name,
            caption: "",
            linkImage: "",
            rel: "nofollow",
            target: "_self",
        });
        const data = resp?.data?.data?.data || resp?.data;
        return data?.url || null;
    } catch {
        return null;
    }
};

const urlToFile = async (url: string, filename: string): Promise<File | null> => {
    try {
        const res = await fetch(url, { mode: 'cors' });
        const blob = await res.blob();
        const type = blob.type || 'image/png';
        return new File([blob], filename, { type });
    } catch {
        return null;
    }
};

const extractFilenameFromUrl = (url: string): string | null => {
    try {
        const u = new URL(url);
        const pathname = u.pathname;
        const name = pathname.split('/').pop() || '';
        return name || null;
    } catch {
        return null;
    }
};
